import React, {useState, useEffect} from 'react'
import {
  Box,
  Checkbox,
  FormControlLabel,
  FormControl,
  Select,
  MenuItem,
  Typography,
  Grid,
  Paper,
  TextField,
  Autocomplete,
} from '@mui/material'
import {
  AVAILABLE_ACTIVITIES,
  ASSISTANCE_LEVELS,
  NOT_ATTEMPTED_REASONS,
} from '../PHYSICAL_THERAPIST_CONSTANT'

interface ActivityAssessment {
  activity: string
  levelOfAssistance: string
  notAttemptedReason: string
}

interface CurrentLevelOfFunctionFormProps {
  value: {
    availableActivities: string[]
    assistanceLevels: string[]
    notAttemptedReasons: string[]
    patientHasMobilityVehicle: string
    wheelchairScooterTypes: string[]
    activityAssessments: ActivityAssessment[]
  }
  onChange: (value: any) => void
  disabled?: boolean
  isEdit?: boolean
}

const CurrentLevelOfFunctionForm: React.FC<CurrentLevelOfFunctionFormProps> = ({
  value,
  onChange,
  disabled = false,
  isEdit,
}) => {
  const [selectedActivities, setSelectedActivities] = useState<string[]>(
    value?.availableActivities || []
  )
  const [activityAssessments, setActivityAssessments] = useState<ActivityAssessment[]>(
    value?.activityAssessments || []
  )
  const [patientHasMobilityVehicle, setPatientHasMobilityVehicle] = useState(
    value?.patientHasMobilityVehicle || ''
  )
  const [wheelchairScooterTypes, setWheelchairScooterTypes] = useState<string[]>(
    value?.wheelchairScooterTypes || []
  )

  useEffect(() => {
    const newAssessments: ActivityAssessment[] = AVAILABLE_ACTIVITIES.map((activity) => {
      const existingAssessment = activityAssessments.find((aa) => aa.activity === activity)
      return {
        activity,
        levelOfAssistance: existingAssessment?.levelOfAssistance || '',
        notAttemptedReason: existingAssessment?.notAttemptedReason || '',
      }
    }).filter((aa) => selectedActivities.includes(aa.activity))

    setActivityAssessments(newAssessments)
  }, [selectedActivities])

  useEffect(() => {
    const updatedValue = {
      availableActivities: selectedActivities,
      assistanceLevels: value?.assistanceLevels || [],
      notAttemptedReasons: value?.notAttemptedReasons || [],
      patientHasMobilityVehicle,
      wheelchairScooterTypes,
      activityAssessments,
    }
    onChange(updatedValue)
  }, [
    selectedActivities,
    activityAssessments,
    patientHasMobilityVehicle,
    wheelchairScooterTypes,
  ])

  const handleActivityToggle = (activity: string) => {
    if (selectedActivities.includes(activity)) {
      setSelectedActivities(selectedActivities.filter((a) => a !== activity))
    } else {
      setSelectedActivities([...selectedActivities, activity])
    }
  }

  const handleAssessmentChange = (
    activity: string,
    field: keyof ActivityAssessment,
    newValue: any
  ) => {
    setActivityAssessments((prev) =>
      prev.map((aa) => (aa.activity === activity ? {...aa, [field]: newValue} : aa))
    )
  }

  const handleWheelchairTypeToggle = (type: string) => {
    if (wheelchairScooterTypes.includes(type)) {
      setWheelchairScooterTypes(wheelchairScooterTypes.filter((t) => t !== type))
    } else {
      setWheelchairScooterTypes([...wheelchairScooterTypes, type])
    }
  }

  return (
    <Box sx={{width: '100%', paddingLeft: '1rem', paddingRight: '1rem', mb: 2}}>
      <Typography variant='h6' sx={{mb: 2, fontWeight: 'bold'}} color='primary'>
        Current Level of Function
      </Typography>
      {/* Mobility Vehicle Section */}
      <Paper sx={{p: 2, mb: 3}}>
        <Typography variant='subtitle1' sx={{mb: 2, fontWeight: 'bold',fontSize: '14px'}} color = 'primary'>
          Patient Mobility Vehicle
        </Typography>
        <Autocomplete
          disablePortal
          id='patientHasMobilityVehicle'
          size='small'
          options={[
            {label: 'YES', value: 'YES'},
            {label: 'NO', value: 'NO'},
          ]}
          isOptionEqualToValue={(option, value) => {
            if (!value || !option) return false
            if (typeof value === 'string') {
              return option.value === value || option.label === value
            }
            return option.value === value.value
          }}
          forcePopupIcon={true}
          disabled={disabled}
          renderInput={(params) => (
            <TextField
              {...params}
              required={false}
              label='Does the patient have a mobility vehicle (wheelchair/scooter)?'
            />
          )}
          value={
            patientHasMobilityVehicle
              ? {label: patientHasMobilityVehicle, value: patientHasMobilityVehicle}
              : null
          }
          onChange={(event, newValue) => {
            const selectedValue = newValue ? newValue.value : ''
            setPatientHasMobilityVehicle(selectedValue)
          }}
        />

        {patientHasMobilityVehicle === 'YES' && (
          <Box>
            <Typography variant='body2' sx={{mb: 1}}>
              Type of wheelchair/scooter:
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={wheelchairScooterTypes.includes('Manual')}
                  onChange={() => handleWheelchairTypeToggle('Manual')}
                  disabled={disabled}
                />
              }
              label='Manual'
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={wheelchairScooterTypes.includes('Motorized')}
                  onChange={() => handleWheelchairTypeToggle('Motorized')}
                  disabled={disabled}
                />
              }
              label='Motorized'
            />
          </Box>
        )}
      </Paper>

      {/* Available Activities Section */}
      <Paper sx={{p: 2, mb: 3}}>
        <Typography variant='subtitle1' sx={{mb: 2, fontWeight: 'bold',fontSize: '16px'}} color = 'primary'>
          Available Mobility Activities
        </Typography>
        <Grid container spacing={2}>
          {AVAILABLE_ACTIVITIES.map((activity) => (
            <Grid item xs={12} sm={6} md={4} key={activity}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={selectedActivities.includes(activity)}
                    onChange={() => handleActivityToggle(activity)}
                    disabled={disabled}
                  />
                }
                label={activity}
              />
            </Grid>
          ))}
        </Grid>
      </Paper>

      {/* Activity Assessments Section */}
      {selectedActivities.length > 0 && (
        <Paper sx={{p: 2}}>
          <Typography variant='subtitle1' sx={{mb: 1, fontWeight: 'bold', fontSize: '16px'}} color = 'primary'>
            Activity Assessments
          </Typography>

          {selectedActivities.map((activity) => {
            const assessment = activityAssessments.find((aa) => aa.activity === activity)
            return (
              <Box key={activity} sx={{ p: 1}}>
                <Typography variant='subtitle2' sx={{mb: 1, fontWeight: 'bold'}} color = 'primary'>
                  {activity}
                </Typography>

                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Autocomplete
                      disablePortal
                      id={`levelOfAssistance-${activity}`}
                      size='small'
                      options={ASSISTANCE_LEVELS.map((level) => ({label: level, value: level}))}
                      isOptionEqualToValue={(option, value) => {
                        if (!value || !option) return false
                        if (typeof value === 'string') {
                          return option.value === value || option.label === value
                        }
                        return option.value === value.value
                      }}
                      forcePopupIcon={true}
                      disabled={disabled}
                      renderInput={(params) => (
                        <TextField {...params} required={false} label='Level of Assistance' />
                      )}
                      value={
                        assessment?.levelOfAssistance
                          ? {
                              label: assessment.levelOfAssistance,
                              value: assessment.levelOfAssistance,
                            }
                          : null
                      }
                      onChange={(event, newValue) => {
                        const selectedValue = newValue ? newValue.value : ''
                        handleAssessmentChange(activity, 'levelOfAssistance', selectedValue)
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Autocomplete
                      disablePortal
                      id={`notAttemptedReason-${activity}`}
                      size='small'
                      options={NOT_ATTEMPTED_REASONS.map((reason) => ({
                        label: reason,
                        value: reason,
                      }))}
                      isOptionEqualToValue={(option, value) => {
                        if (!value || !option) return false
                        if (typeof value === 'string') {
                          return option.value === value || option.label === value
                        }
                        return option.value === value.value
                      }}
                      forcePopupIcon={true}
                      disabled={disabled}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          required={false}
                          label='Not Attempted Reason (if applicable)'
                        />
                      )}
                      value={
                        assessment?.notAttemptedReason
                          ? {
                              label: assessment.notAttemptedReason,
                              value: assessment.notAttemptedReason,
                            }
                          : null
                      }
                      onChange={(event, newValue) => {
                        const selectedValue = newValue ? newValue.value : ''
                        handleAssessmentChange(activity, 'notAttemptedReason', selectedValue)
                      }}
                    />
                  </Grid>

                  
                </Grid>
              </Box>
            )
          })}
        </Paper>
      )}
    </Box>
  )
}

export default CurrentLevelOfFunctionForm
